import type {
  Campaign,
  CampaignDisplayStatus,
  CampaignFilterInput,
  CampaignInsertInput,
  CampaignItem,
  CampaignSortInput,
  CampaignStatus,
  CampaignType,
  CampaignsFilters,
  CampaignsSortKey,
} from '../types/campaigns.types';

const LEGACY_PUBLISHED_STATUSES = ['Published', 'Sent', 'Archived'] as const;
const LEGACY_DRAFT_STATUSES = ['Draft', 'Scheduled', 'Active', 'Cancelled'] as const;

const CAMPAIGN_TYPES: CampaignType[] = ['Regular', 'Scheduled', 'Recurring'];

const normalizeEnum = <T extends string>(
  value: string | null | undefined,
  allowed: T[],
  fallback: T
): T => {
  if (!value) return fallback;
  const match = allowed.find((item) => item.toLowerCase() === value.trim().toLowerCase());
  return match ?? fallback;
};

const toOptionalString = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const toOptionalStringArray = (value: string[] | null | undefined): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const items = value.map((item) => item?.trim()).filter((item): item is string => Boolean(item));
  return items.length > 0 ? items : undefined;
};

export const formatCampaignDate = (value: string | null | undefined): string | null => {
  if (!value?.trim()) return null;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.trim();

  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const resolveCampaignStatus = (
  item: Pick<CampaignItem, 'Status' | 'IsArchived'>
): CampaignStatus => {
  if (item.IsArchived === true) return 'Published';

  const raw = item.Status?.trim().toLowerCase();
  if (raw === 'sent' || raw === 'archived' || raw === 'published') {
    return 'Published';
  }

  return 'Draft';
};

export const extractCampaignSubject = (item: CampaignItem): string => {
  const subject = item.CampaignMediaDetails?.find((m) => m.Subject?.trim())?.Subject?.trim();
  return subject ?? '';
};

export const shouldClearLegacySubjectAsTemplateName = (
  subject: string,
  templateName: string
): boolean => {
  const normalizedSubject = subject.trim();
  const normalizedTemplateName = templateName.trim();
  return Boolean(normalizedSubject && normalizedTemplateName && normalizedSubject === normalizedTemplateName);
};

export const resolveCampaignDisplayStatus = (
  item: Pick<CampaignItem, 'Status' | 'IsArchived' | 'Type'>
): CampaignDisplayStatus => {
  const raw = item.Status?.trim().toLowerCase() ?? '';
  if (item.IsArchived === true) return 'Sent';
  if (raw === 'sent' || raw === 'archived' || raw === 'published') return 'Sent';
  if (raw === 'sending') return 'Sending';
  if (raw === 'failed') return 'Failed';
  if (raw === 'cancelled' || raw === 'canceled') return 'Cancelled';
  if (raw === 'scheduled') return 'Scheduled';
  const type = normalizeEnum(item.Type, CAMPAIGN_TYPES, 'Regular');
  if (type === 'Scheduled' && resolveCampaignStatus(item) === 'Draft') return 'Scheduled';
  if (resolveCampaignStatus(item) === 'Published') return 'Sent';
  return 'Draft';
};

const resolveScheduledOrSentAt = (item: CampaignItem, status: CampaignStatus): string | null => {
  if (status === 'Published') {
    return formatCampaignDate(item.DatePublished ?? item.ScheduleTime ?? item.StartDate);
  }
  return formatCampaignDate(item.ScheduleTime ?? item.StartDate ?? item.DatePublished);
};

export const mapCampaignItemToCampaign = (item: CampaignItem): Campaign => {
  const status = resolveCampaignStatus(item);
  const type = normalizeEnum(item.Type, CAMPAIGN_TYPES, 'Regular');
  const audienceRaw = item.TotalRecipent;

  return {
    id: item.ItemId,
    name: item.Name?.trim() || 'Untitled campaign',
    subject: extractCampaignSubject(item),
    channel: 'Email',
    displayStatus: resolveCampaignDisplayStatus(item),
    audienceSize:
      audienceRaw === null || audienceRaw === undefined ? null : Number(audienceRaw),
    sendableCount: null,
    scheduledOrSentAt: resolveScheduledOrSentAt(item, status),
    type,
    status,
  };
};

export const buildCloneCampaignName = (name: string | null | undefined): string => {
  const baseName = name?.trim() || 'Untitled campaign';
  return `${baseName} clone`;
};

export const buildCloneCampaignInput = (campaign: CampaignItem): CampaignInsertInput => {
  const mediaDetails = (campaign.CampaignMediaDetails ?? [])
    .map((media) => ({
      MediaId: toOptionalString(media.MediaId),
      CampaignType: toOptionalString(media.CampaignType),
      Entity: toOptionalString(media.Entity),
      Filter: toOptionalString(media.Filter),
      Subject: toOptionalString(media.Subject),
      Templet: toOptionalString(media.Templet),
    }))
    .filter((media) =>
      Boolean(
        media.MediaId ||
          media.CampaignType ||
          media.Entity ||
          media.Filter ||
          media.Subject ||
          media.Templet
      )
    );

  return {
    Name: buildCloneCampaignName(campaign.Name),
    Type: normalizeEnum(campaign.Type, CAMPAIGN_TYPES, 'Regular'),
    Status: 'Draft',
    IsArchived: false,
    CampaignActionType: toOptionalString(campaign.CampaignActionType),
    CampaignEvent: toOptionalString(campaign.CampaignEvent),
    CampaignMediaDetails: mediaDetails.length > 0 ? mediaDetails : undefined,
    CampaignPublishGraphQLQuery: toOptionalString(campaign.CampaignPublishGraphQLQuery),
    CronExpression: toOptionalString(campaign.CronExpression),
    CustomFromAddress: toOptionalString(campaign.CustomFromAddress),
    EmailPublishingDetailForTargetAll: toOptionalString(campaign.EmailPublishingDetailForTargetAll),
    EndDate: toOptionalString(campaign.EndDate),
    IsASignUpCampaign: campaign.IsASignUpCampaign ?? undefined,
    IsCampaignTargetAll: campaign.IsCampaignTargetAll ?? undefined,
    IsGraphQLQueryBySegmentWise: campaign.IsGraphQLQueryBySegmentWise ?? undefined,
    IsMarkedToDelete: false,
    IsRecurringOrScheduled: campaign.IsRecurringOrScheduled ?? undefined,
    LoginLinkExpiryDate: toOptionalString(campaign.LoginLinkExpiryDate),
    MetaTags: toOptionalStringArray(campaign.MetaTags),
    Tags: toOptionalStringArray(campaign.Tags),
    Priority: toOptionalString(campaign.Priority),
    RecipientsToAddForTargetAll: toOptionalStringArray(campaign.RecipientsToAddForTargetAll),
    RecipientsToRemoveForTargetAll: toOptionalStringArray(campaign.RecipientsToRemoveForTargetAll),
    ScheduledorRecurringStatus: toOptionalString(campaign.ScheduledorRecurringStatus),
    ScheduleTime: toOptionalString(campaign.ScheduleTime),
    TimeZone: toOptionalString(campaign.TimeZone),
    SegmentationType: toOptionalString(campaign.SegmentationType),
    StartDate: toOptionalString(campaign.StartDate),
    TargetAudience: toOptionalString(campaign.TargetAudience),
    TenantId: toOptionalString(campaign.TenantId),
    TimesSent: 0,
    TotalRecipent: Number(campaign.TotalRecipent ?? 0),
  };
};

export const buildCampaignsWhere = (
  search: string,
  filters: CampaignsFilters
): CampaignFilterInput => {
  const clauses: CampaignFilterInput[] = [];

  const term = search.trim();
  if (term) {
    clauses.push({ Name: { contains: term } });
  }

  if (filters.statuses.length > 0) {
    const statusClauses = filters.statuses.map<CampaignFilterInput>((status) => {
      if (status === 'Sent' || status === 'Sending' || status === 'Failed' || status === 'Cancelled') {
        return {
          or: [
            { Status: { in: [...LEGACY_PUBLISHED_STATUSES] } },
            { IsArchived: { eq: true } },
          ],
        };
      }

      return {
        and: [
          { Status: { in: [...LEGACY_DRAFT_STATUSES] } },
          { IsArchived: { eq: false } },
          status === 'Scheduled'
            ? { Type: { eq: 'Scheduled' } }
            : { Type: { neq: 'Scheduled' } },
        ],
      };
    });
    clauses.push({ or: statusClauses });
  }

  if (clauses.length === 0) {
    return {};
  }

  if (clauses.length === 1) {
    return clauses[0];
  }

  return { and: clauses };
};

export const buildCampaignsOrder = (sortKey: CampaignsSortKey): CampaignSortInput[] => {
  switch (sortKey) {
    case 'name':
      return [{ field: 'Name', direction: 'ASC' }];
    case 'createdAt':
      return [{ field: 'CreatedDate', direction: 'DESC' }];
    case 'scheduledAt':
      return [
        { field: 'ScheduleTime', direction: 'DESC' },
        { field: 'CreatedDate', direction: 'DESC' },
      ];
    case 'updatedAt':
    default:
      return [
        { field: 'LastUpdatedDate', direction: 'DESC' },
        { field: 'CreatedDate', direction: 'DESC' },
      ];
  }
};
